import {
  colors,
  easingTokens,
  radius,
  transitionTokens,
} from '@ardoq/design-tokens';
import {
  insetSpotlightPulse,
  SPOTLIGHT_PULSE_DURATION_MS,
} from '@ardoq/style-helpers';
import styled, { css, keyframes } from 'styled-components';

import { useElementBorderRadius } from './hooks';
import {
  type CornerRadii,
  doRectsOverlap,
  inflateWithinViewport,
  toBoundingRect,
  toDimClipPath,
  toRoundedRectPath,
} from './spotlightGeometry';
import type { Rect } from './types';

/**
 * The spotlight is two independent layers over the same target, not one element:
 *
 * 1. `DimLayer` — one translucent sheet across the viewport, with a hole per
 *    highlighted element cut out of its `clip-path`.
 * 2. `Highlight` — one absolutely positioned box per element, sitting in that
 *    hole, whose `::after` draws the brand ring and its breathing glow.
 *
 * They are drawn by different mechanisms — an SVG path versus a CSS
 * `border-radius` — so nothing makes them agree automatically. Both are fed the
 * same rect and the same `border-radius` value, and `toHighlightCornerRadii`
 * exists to resolve that value into the pixel radii the path needs. When the two
 * disagree, the hole and the ring trace different corners and undimmed patches
 * appear around the target.
 */

/** Dimming applied around the spotlight / over the whole screen on modal steps. */
const DIM = 'rgba(0, 0, 0, 0.4)';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/**
 * Full-viewport layer. When dimming the whole screen (modal steps) it carries
 * its own translucent background. For anchored steps it stays transparent — it
 * is only there to swallow clicks — and the DimLayer below carries the dimming.
 */
const Backdrop = styled.div<{ $dimAll: boolean; $allowInteraction: boolean }>`
  position: fixed;
  inset: 0;
  pointer-events: ${({ $allowInteraction }) =>
    $allowInteraction ? 'none' : 'auto'};
  background: ${({ $dimAll }) => ($dimAll ? DIM : 'transparent')};
  ${({ $dimAll }) =>
    $dimAll &&
    css`
      animation: ${fadeIn} ${transitionTokens.large} ${easingTokens.cubicOut}
        both;
    `};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Carries the dimming for anchored steps: a full-viewport translucent sheet with
 * a hole punched out per highlighted element, so a step can light up two things
 * at once. Purely decorative — the Backdrop above it owns pointer events.
 */
const DimLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  background: ${DIM};
`;

/**
 * Sits over a highlighted element, in the hole the DimLayer leaves for it; the
 * `::after` carries the brand ring + a soft breathing glow. The box glides to
 * its new position between steps so it tracks the spotlight smoothly. When
 * `$track` is set the glide is dropped: the box is being fed a fresh rect every
 * frame as its anchor settles, and a running transition would trail it.
 */
const glideTransition = ['top', 'left', 'width', 'height']
  .map(
    property =>
      `${property} ${transitionTokens.medium} ${easingTokens.cubicInOut}`
  )
  .join(', ');

const Highlight = styled.div<{ $track: boolean }>`
  position: fixed;
  pointer-events: none;
  transition: ${({ $track }) => ($track ? 'none' : glideTransition)};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 0 0 2px ${colors.brand500};
    animation: ${insetSpotlightPulse} ${SPOTLIGHT_PULSE_DURATION_MS}ms
      ${easingTokens.cubicInOut} infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &::after {
      animation: none;
    }
  }
`;

export type SpotlightHighlight = {
  /** Element to cut out and highlight; its corners shape the hole and the ring. */
  element: HTMLElement;
  /** The element's viewport rect, measured by the caller. */
  rect: Rect;
};

export type TourSpotlightProps = {
  dimAll: boolean;
  allowInteraction: boolean;
  /** Follow the anchor frame-by-frame instead of gliding; see {@link Highlight}. */
  trackMovingAnchor: boolean;
} & (
  | {
      /**
       * The step's anchor. Pass `null` with `dimAll` to dim the whole screen (modal
       * steps), or `null` alone for a transparent blocker while a target resolves.
       */
      primary: null;
      /**
       * A second element the step's copy also points at — highlighted alongside the
       * anchor, which the card still points its arrow at. `null` for the ordinary
       * one-element step.
       */
      secondary: null;
    }
  | {
      primary: SpotlightHighlight;
      secondary: null;
    }
  | {
      primary: SpotlightHighlight;
      secondary: SpotlightHighlight;
    }
);

/**
 * Engines disagree on how they serialise the `border-radius` shorthand, so a
 * zero-cornered element reaches us as any of `"0px"`, `"0px 0px"`, `"0%"` or the
 * elliptical `"0px 0px 0px 0px / 0px 0px 0px 0px"`. Compare lengths rather than
 * strings, and only the horizontal radii before the slash: those alone decide
 * whether any corner is rounded.
 */
const isSquareCorners = (borderRadius: string): boolean =>
  borderRadius
    .split('/')[0]
    .trim()
    .split(/\s+/)
    .every(component => parseFloat(component) === 0);

/**
 * Trace the target's own corners so the ring reads as the shape of the thing
 * it highlights; round the ring ourselves when the target has square corners.
 */
export const toHighlightBorderRadius = (
  targetBorderRadius: string | null
): string =>
  !targetBorderRadius || isSquareCorners(targetBorderRadius)
    ? radius.medium
    : targetBorderRadius;

const FALLBACK_RADIUS_PX = parseFloat(radius.medium);

/**
 * A percentage radius resolves against the box the ring is drawn on, so it needs
 * that box to become pixels. Resolving against the shorter side keeps the hole
 * no smaller than the ring: a square target — the case that reaches us, since
 * `50%` is how the codebase draws avatars and round icon buttons — comes out as
 * the exact circle CSS draws. Anything the engine leaves unresolved, such as
 * `calc(50% - 4px)`, falls back rather than poisoning the path with `NaN`.
 */
const toRadiusPx = (component: string, basis: number): number => {
  const parsed = parseFloat(component);
  if (!Number.isFinite(parsed)) return FALLBACK_RADIUS_PX;
  return component.includes('%') ? (parsed / 100) * basis : parsed;
};

/**
 * The hole's corner radii, in pixels, expanded from the same `border-radius`
 * value the ring is drawn with, against the same rect, so the two trace the same
 * corners. Follows the CSS shorthand's omission rules (`tr` defaults to `tl`,
 * `br` to `tl`, `bl` to `tr`), reading only the horizontal radii before the
 * slash.
 */
export const toHighlightCornerRadii = (
  borderRadius: string,
  rect: Rect
): CornerRadii => {
  const basis = Math.min(rect.width, rect.height);
  const [topLeft = FALLBACK_RADIUS_PX, topRight, bottomRight, bottomLeft] =
    borderRadius
      .split('/')[0]
      .trim()
      .split(/\s+/)
      .map(component => toRadiusPx(component, basis));
  return {
    topLeft,
    topRight: topRight ?? topLeft,
    bottomRight: bottomRight ?? topLeft,
    bottomLeft: bottomLeft ?? topRight ?? topLeft,
  };
};

type SpotlightHole = {
  key: string;
  rect: Rect;
  borderRadius: string;
};

const toHole = (
  key: string,
  highlight: SpotlightHighlight | null,
  targetBorderRadius: string | null
): SpotlightHole | null => {
  if (!highlight) return null;
  return {
    key,
    rect: inflateWithinViewport(
      highlight.rect,
      window.innerWidth,
      window.innerHeight
    ),
    borderRadius: toHighlightBorderRadius(targetBorderRadius),
  };
};

const toHolePath = (hole: SpotlightHole): string =>
  toRoundedRectPath(
    hole.rect,
    toHighlightCornerRadii(hole.borderRadius, hole.rect)
  );

/**
 * The subpaths cut out of the dimming layer. Overlapping holes — a highlighted
 * element nested in another, or two anchors close enough that the spotlight's
 * padding makes them touch — become one hole covering both: their intersection
 * would otherwise be dimmed back in by the even-odd fill. Each element still
 * gets its own ring on top.
 */
export const toDimHolePaths = (holes: SpotlightHole[]): string[] => {
  const [first, second] = holes;
  if (first && second && doRectsOverlap(first.rect, second.rect)) {
    return [
      toRoundedRectPath(toBoundingRect(first.rect, second.rect), {
        topLeft: FALLBACK_RADIUS_PX,
        topRight: FALLBACK_RADIUS_PX,
        bottomRight: FALLBACK_RADIUS_PX,
        bottomLeft: FALLBACK_RADIUS_PX,
      }),
    ];
  }
  return holes.map(toHolePath);
};

export const TourSpotlight = ({
  primary,
  secondary,
  dimAll,
  allowInteraction,
  trackMovingAnchor,
}: TourSpotlightProps) => {
  const primaryBorderRadius = useElementBorderRadius(primary?.element ?? null);
  const secondaryBorderRadius = useElementBorderRadius(
    secondary?.element ?? null
  );
  const holes = dimAll
    ? []
    : [
        toHole('primary', primary, primaryBorderRadius),
        toHole('secondary', secondary, secondaryBorderRadius),
      ].flatMap(hole => (hole ? [hole] : []));

  return (
    <Backdrop $dimAll={dimAll} $allowInteraction={allowInteraction}>
      {holes.length > 0 && (
        <>
          <DimLayer
            style={{
              clipPath: toDimClipPath(
                toDimHolePaths(holes),
                window.innerWidth,
                window.innerHeight
              ),
            }}
          />
          {holes.map(hole => (
            <Highlight
              key={hole.key}
              $track={trackMovingAnchor}
              style={{ ...hole.rect, borderRadius: hole.borderRadius }}
            />
          ))}
        </>
      )}
    </Backdrop>
  );
};
